/**
 * Share token helper — mints a signed token via BE generateShareToken so the URL
 * the admin opens / copies for client/contractor consumption is tamper-evident.
 *
 * Soft mode (default) on the BE will tolerate missing/invalid tokens. Once
 * SHARE_TOKEN_STRICT=true is set in BE env, only URLs minted via this helper
 * will be accepted by the shareable endpoints.
 *
 * Base inventory: `src/api/_INVENTORY.md` section "Auth" — addresses the
 * MIGRATION-DEBT item "tempToken validation for shareable-page endpoints".
 */
import { http } from "./client";

export type ShareTokenScope =
  | "estimate-page"
  | "deposit-invoice-page"
  | "invoice-page"
  | "po-page"
  | "sow-page";

/**
 * POST /generateShareToken — returns { token, scope, eventId }. Best-effort:
 * if the call fails, falls back to building the URL without the token (soft mode).
 */
export async function buildShareableUrl(opts: {
  baseUrl: string;
  scope: ShareTokenScope;
  eventId: string;
  /** Extra query params to append (e.g. configurationId). */
  extraParams?: Record<string, string | number | null | undefined>;
}): Promise<string> {
  const qp = new URLSearchParams();
  qp.set("eventId", opts.eventId);
  for (const [k, v] of Object.entries(opts.extraParams || {})) {
    if (v === null || v === undefined) continue;
    qp.set(k, String(v));
  }
  try {
    const { response } = await http({
      method: "POST",
      url: "generateShareToken",
      data: { eventId: opts.eventId, scope: opts.scope },
      isToken: true,
    });
    const token = response?.data?.data?.token || response?.data?.token;
    if (token) qp.set("shareToken", String(token));
  } catch {
    /* soft-mode: missing token is tolerated by BE guard */
  }
  return `${opts.baseUrl}?${qp.toString()}`;
}
